import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import DataGrid from "../components/common/DataGrid";
import { SelectBox } from "../components/common/SelectBox";
import PageContent from "../components/pageContent";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { FormatString } from "../helpers/stringHelpers";
import { checkCrud } from "../helpers/userRolesHelper";
import DiscountPopup from "../popups/DiscountPopup";
import { TranslationProvider } from "../providers/TranslationContext";
import { addNotification } from "../redux/actions";
import { apiDeleteDiscount, apiGetDiscountGroups, apiGetDiscounts, apiGetDiscountsForSeller } from "../services/DiscountsService";
import { apiGetAllSellers } from "../services/SellersService";

interface Props {
    contextLevel: string;
}

const entityName = 'discount';
const translationPrefix = 'Discounts';
const translationModel = { prefix: translationPrefix };

const SlevyPage = (props: Props) => {
    const { t } = useTranslation(undefined, { keyPrefix: translationPrefix });
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ rows: [] });
    const [discountGroups, setDiscountGroups] = useState<any[]>([]);
    const [insludedColumns, setIncludedColumns] = useState<any[]>([]);
    const [popupData, setPopupData] = useState({
        visible: false,
        id: undefined,
    });
    const dispatch = useDispatch();
    const [sellers, setSellers] = useState<any[]>([]);
    const [formData, setFormData] = useState({
        sellerId: null
    });
    const [preloading, setPreloading] = useState(true);

    const [canCreate, setCanCreate] = useState(false);
    const [canRead, setCanRead] = useState(false);
    const [canUpdate, setCanUpdate] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    const userInfo = useSelector((state: any) => state.userInfo);

    const setRoles = () => {
        setCanCreate(checkCrud(entityName, 'create', userInfo));
        setCanRead(checkCrud(entityName, 'read', userInfo));
        setCanUpdate(checkCrud(entityName, 'update', userInfo));
        setCanDelete(checkCrud(entityName, 'delete', userInfo));
        setIsAdmin(checkCrud(entityName, 'admin', userInfo));
    }

    useEffect(() => {
        setRoles();
    }, [userInfo.UserRoles])

    useEffect(() => {
        if (isAdmin) {
            apiGetAllSellers()
                .then((responseData: any) => {
                    let arr = responseData.data.map((element) => {
                        return {
                            value: element.id,
                            label: element.name
                        };
                    });
                    setSellers(arr);
                    let newFormData = formData;
                    newFormData.sellerId = userInfo.SellerId;
                    setFormData(newFormData);
                    setPreloading(false);
                })
                .catch((err: any) => {

                });
        }
        else {
            setPreloading(false);
        }
    }, [userInfo, isAdmin]);

    useEffect(() => {
        if (!preloading) {
            loadData();
        }
    }, [preloading]);

    useEffect(() => {
        if (!preloading) {
            loadData(formData.sellerId);
        }
    }, [formData.sellerId]);

    const loadData = (sellerId?: number | null) => {
        const additionalIsAdmin = checkCrud(entityName, 'admin', userInfo);
        if (additionalIsAdmin) {
            apiGetDiscountsForSeller(sellerId ?? userInfo.SellerId).then((responseData) => {
                processLoadDataResponse(responseData)
            });
        }
        else {
            apiGetDiscounts().then((responseData) => {
                processLoadDataResponse(responseData)
            });
        }
        apiGetDiscountGroups().then((responseData) => {
            setDiscountGroups(responseData.data);
        });
    };

    const processLoadDataResponse = (responseData) => {
        //transform data to include subGrops (A-E) into the model
        //extract additional columns

        let extraColumns: any[] = [];
        responseData?.data[0]?.discountGroups?.forEach((g: any) => {
            extraColumns.push({
                name: g.groupName,
                dataField: `group${g.groupName}`,
                sortable: true,
                filterable: false,
                fieldType: "numberWithGreenForPositiveAndRedForNegative"
            });
        });
        setIncludedColumns(extraColumns);
        let transformedData = responseData.data.map((row: any) => {

            row.discountBaseDecorative = -row.discountBaseDecorative;// inverted value
            row.discountBaseIndustry = -row.discountBaseIndustry;// inverted value
            
            row.discountGroups.forEach((g: any) => {
                const extraColumn = extraColumns.find((c: any) => { return c.name === g.groupName });
                row[extraColumn.dataField] = -g.value; // inverted value
            });

            // resolve area

            if (!row.idProductsGroup || row.idProductsGroup == '0') {
                row.applicableTo = t('OptionAllProducts');
            }
            else {

                if (row.idProductsGroup == '1') { row.applicableTo = t('OptionOnlyDecorative'); };
                if (row.idProductsGroup == '2') { row.applicableTo = t('OptionOnlyRetail'); };

            }

            return row;
        });

        setData({
            rows: transformedData
        });

        setLoading(false);
    }

    const handleDeleteRow = (row) => {
        apiDeleteDiscount(row.id)
            .then((responseData) => {
                dispatch(
                    addNotification({
                        text: FormatString(t('Popup.DeleteSuccess'), row.code),
                        type: "success",
                    })
                );
                loadData(formData.sellerId);

                setLoading(false);
            })
            .catch((err: any) => {
                dispatch(
                    addNotification({
                        text: FormatString(t('Popup.DeleteFailure'), row.code),
                        type: "error",
                    })
                );
            });
    };

    let cols: any[] = [];

    if (!loading) {
        cols =
            [
                {
                    name: t('Fields.sortOrder'),
                    dataField: "sortOrder",
                    isLinkToItem: false,
                    sortable: true,
                    filterable: true,
                    filterType: "text",
                },
                {
                    name: t('Fields.isPrimary'),
                    dataField: "isPrimary",
                    isLinkToItem: false,
                    sortable: false,
                    filterable: false,
                    filterType: "text",
                    fieldType: "boolean"
                },
                {
                    name: t('Fields.showForAllReaders'),
                    dataField: "showToAllReaders",
                    isLinkToItem: false,
                    sortable: false,
                    filterable: false,
                    filterType: "text",
                    fieldType: "boolean"
                },
                {
                    name: t('Fields.name'),
                    dataField: "name",
                    isLinkToItem: false,
                    sortable: true,
                    filterable: true,
                    filterType: "text",
                }, 
                {
                    name: t('Fields.discountForBaseRetail'),
                    dataField: "discountBaseIndustry",
                    sortable: true,
                    filterable: false,
                    filterType: "text",
                    fieldType: "numberWithGreenForPositiveAndRedForNegative"
                },
                  {
                    name: t('Fields.discountForBaseDecorative'),
                    dataField: "discountBaseDecorative",
                    sortable: true,
                    filterable: false,
                    filterType: "text",
                    fieldType: "numberWithGreenForPositiveAndRedForNegative"
                },
                ...insludedColumns,
                { name: t('Fields.noteInternal'), dataField: "noteInternal" },
                {
                    name: t('Fields.modifiedOn'),
                    fieldType: "datetime",
                    dataField: "modifiedOn",
                    sortable: true,
                },
                {
                    name: t('Fields.createdOn'),
                    fieldType: "datetime",
                    dataField: "createdOn",
                    sortable: true,
                },
            ]
    }

    return <TranslationProvider value={translationModel}>
        {popupData.visible && (
            <DiscountPopup
                id={popupData.id}
                refreshData={() => loadData(formData.sellerId)}
                handleCancel={() => {
                    setPopupData({ visible: false, id: undefined });
                }}
                discountGroups={discountGroups}
                sellerId={formData.sellerId}
            />
        )}

        <TopBarWithBreadCrumb
            contextLevel={props.contextLevel}
            pageLevel={t('Label')}
            rightAreaComponent={
                <AddButtonInTopBar
                    setPopupData={setPopupData}
                    text={t('CreateText')}
                    enabled={canCreate}
                />
            }
        />
        <PageContent>
            <>
                {isAdmin && (
                    <SelectBox
                        placeholder={t('Fields.chooseSeller')}
                        dataField={'sellerId'}
                        isDisabled={false}
                        formData={formData}
                        value={formData.sellerId}
                        options={sellers}
                        className="zIndex10"
                        label={t('Fields.seller')}
                        customLeftCols={'col-sm-1'}
                        customRightCols={'col-sm-3'}
                        setStateMethod={setFormData}
                    />
                )}
                {!loading && <DataGrid
                    loading={loading}
                    rows={data.rows}
                    showDeleteButton={canDelete}
                    showEditButton={canUpdate}
                    onDeleteRow={handleDeleteRow}
                    handleEditAction={(id: any) => {
                        setPopupData({ visible: true, id: id });
                    }}
                    cols={cols}
                />
                }
            </>
        </PageContent >
    </TranslationProvider>
}

export default SlevyPage;